import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Button } from '@/components/Button';
import { ConfirmModal } from '@/components/Modal/ConfirmModal';
import { Snackbar } from '@/components/Snackbar';
import { useCancelSubscription } from '@/pages/Subscription/hooks/useCancelSubscription';
import { useStatusCard } from '@/pages/Subscription/hooks/useStatusCard';
import { ROUTE_SUBSCRIPTION_PAYMENT_OPTIONS } from '@/router/routes';
import {
  BorderRadius,
  ButtonVariant,
  Size,
  SnackbarVariant,
} from '@/types/components';
import { SubscriptionPaymentKind } from '@/types/user';
import { formatDate } from '@/utils/date';

const BADGE_CLASS =
  'w-fit rounded-xl px-2 py-0.5 text-caption-12 text-foreground';

export const StatusCard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    isActive,
    isSmsSubscriber,
    isCardSubscriber,
    canPurchase,
    showEndDate,
    endDate,
    canCancel,
    cancelKind,
  } = useStatusCard();

  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    message: '',
    variant: SnackbarVariant.Success,
  });

  const isCardCancel = cancelKind === SubscriptionPaymentKind.Card;
  const buttonLabelKey = !isActive
    ? 'subscription.intro.inactiveButton'
    : isSmsSubscriber
      ? 'subscription.intro.activeButton'
      : 'subscription.intro.prolongButton';

  const showSnackbar = (message: string, variant: SnackbarVariant) => {
    setSnackbar({ message, variant });
    setIsSnackbarOpen(true);
  };

  const { cancelSubscription, isPending } = useCancelSubscription({
    onSuccess: (kind) => {
      setIsCancelOpen(false);
      showSnackbar(
        t(
          kind === SubscriptionPaymentKind.Card
            ? 'subscription.intro.cancel.cardSuccess'
            : 'subscription.intro.cancel.smsSuccess',
        ),
        SnackbarVariant.Success,
      );
    },
    onError: () =>
      showSnackbar(t('global.errorOccurred'), SnackbarVariant.Error),
  });

  return (
    <>
      <div className="flex flex-col gap-6 rounded-2xl bg-card p-6 shadow-card">
        <div className="flex flex-col gap-3">
          {isActive && (
            <p className="text-[18px]/[24px] font-semibold text-foreground md:text-[20px]/[28px] md:font-medium">
              {t('subscription.intro.status.activePrefix')}
              <span className="text-green">
                {t('subscription.intro.status.activeSuffix')}
              </span>
            </p>
          )}
          {!isActive && (
            <p className="text-[18px]/[24px] font-semibold text-foreground md:text-[20px]/[28px] md:font-medium">
              {t('subscription.intro.status.inactivePrefix')}
              <span className="text-muted-foreground">
                {t('subscription.intro.status.inactiveSuffix')}
              </span>
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {isSmsSubscriber && (
              <span className={`${BADGE_CLASS} bg-purple-soft`}>
                {t('subscription.intro.status.smsBadge')}
              </span>
            )}
            {isCardSubscriber && (
              <span className={`${BADGE_CLASS} bg-purple-soft`}>
                {t('subscription.intro.status.cardBadge')}
              </span>
            )}
            {showEndDate && endDate && (
              <span className={`${BADGE_CLASS} bg-green-soft`}>
                {t('subscription.intro.status.activeUntil', {
                  date: formatDate(endDate),
                })}
              </span>
            )}
          </div>
        </div>

        {(canPurchase || canCancel) && (
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            {canPurchase && (
              <Button
                variant={ButtonVariant.Primary}
                size={Size.Medium}
                radius={BorderRadius.Pill}
                className="w-full md:w-fit"
                onClick={() => navigate(ROUTE_SUBSCRIPTION_PAYMENT_OPTIONS)}
              >
                {t(buttonLabelKey)}
              </Button>
            )}
            {canCancel && (
              <button
                type="button"
                onClick={() => setIsCancelOpen(true)}
                className="cursor-pointer self-center py-1 text-subhead-14 text-primary md:self-auto"
              >
                {t('subscription.intro.cancelSubscription')}
              </button>
            )}
          </div>
        )}
      </div>

      <ConfirmModal
        simpleView
        isOpen={isCancelOpen}
        title={t(
          isCardCancel
            ? 'subscription.intro.cancel.cardTitle'
            : 'subscription.intro.cancel.smsTitle',
        )}
        description={t(
          isCardCancel
            ? 'subscription.intro.cancel.cardDescription'
            : 'subscription.intro.cancel.smsDescription',
        )}
        isPending={isPending}
        onClose={() => setIsCancelOpen(false)}
        onConfirm={() => cancelSubscription(cancelKind)}
      />

      <Snackbar
        isOpen={isSnackbarOpen}
        compact
        message={snackbar.message}
        variant={snackbar.variant}
        onDismiss={() => setIsSnackbarOpen(false)}
      />
    </>
  );
};
